/**
 * Which captured exceptions are worth storing.
 *
 * PostHog error tracking is only useful if the volume means something. In the
 * week of 2026-08-04, 164 exceptions were captured and roughly a third could
 * never be acted on: browser extensions failing in their own injected code,
 * expected fetch aborts, and errors from developers' own localhost servers.
 * That noise is what a 58% week-over-week "spike" was mostly made of.
 *
 * Extracted from the inline before_send callback so the matching can be tested
 * — the previous inline version filtered on the wrong field and silently
 * matched nothing for months.
 */

type CapturedException = { type?: string; value?: string };

export type CapturableEvent = {
  event?: string;
  properties?: {
    $exception_list?: CapturedException[];
    [key: string]: unknown;
  };
} | null;

/**
 * Scripts injected into the page by the visitor's browser or extensions.
 *
 * These run in the page's context, so the browser reports OUR url as the
 * filename and there is nothing in our bundle to fix — verified 2026-08-06:
 * none of these identifiers appear anywhere in our source or shipped chunks,
 * and all 42 occurrences came from a single iOS device inside 90 seconds.
 *
 * `__firefox__` is Firefox-for-iOS's injected namespace (reader mode, its
 * YouTube quality script). `ethereum` is a crypto wallet extension probing for
 * a provider. Both are the visitor's software, not ours.
 *
 * `Object Not Found Matching Id` is an extension content script failing to
 * reach its own background page. It arrives as a bare promise rejection with
 * no stacktrace at all and `synthetic: true`, so there is no frame to fix.
 * Verified 2026-08-18 to the same standard as the entries above: the string
 * appears nowhere in src/ or the shipped chunks. It produced 36 of the 106
 * exceptions in the week to 2026-08-17 from two visitors, and was the whole of
 * that week's apparent spike.
 */
const INJECTED_SCRIPT_PATTERNS = [
  /__firefox__/,
  /window\.ethereum/,
  /refresh_youtube_quality/,
  /Object Not Found Matching Id/,
];

/**
 * Aborted requests are control flow, not failure: every in-flight fetch throws
 * one when the user navigates away mid-request.
 *
 * Matched on the MESSAGE, not the type. The original filter compared
 * `type === "AbortError"`, but the browser reports these as a DOMException
 * whose *message* begins with "AbortError" — so the check never fired and the
 * events it existed to suppress went through anyway.
 */
const BENIGN_MESSAGE_PATTERNS = [
  /^AbortError\b/,
  /^Fetch is aborted$/,
  /The user aborted a request/,
];

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".local")
  );
}

/**
 * True when this event should be dropped instead of sent.
 *
 * `hostname` is passed in rather than read from `window` so the rules can be
 * tested without a DOM.
 */
export function shouldDropException(event: CapturableEvent, hostname: string): boolean {
  if (event?.event !== "$exception") return false;

  // Developer machines must not report into the production project. Local work
  // was showing up beside real user errors, including transient failures that
  // only ever existed in a dev server mid-edit.
  if (isLocalHost(hostname)) return true;

  const exceptions = event.properties?.$exception_list ?? [];
  return exceptions.some((exception) => {
    const message = exception?.value ?? "";
    const type = exception?.type ?? "";
    return (
      INJECTED_SCRIPT_PATTERNS.some((pattern) => pattern.test(message)) ||
      BENIGN_MESSAGE_PATTERNS.some((pattern) => pattern.test(message)) ||
      type === "AbortError"
    );
  });
}
